import { z } from 'zod';
import { UuidSchema } from './uuid';

export const LandingSurfaceTypeSchema = z.enum(['challenge', 'campaign', 'site']);

export const AttributionContextSchema = z.object({
  landing_touch_id: UuidSchema,
  landing_surface_type: LandingSurfaceTypeSchema,
  landing_surface_key: z.string().nullable().optional(),
  challenge_share_id: UuidSchema.nullable().optional(),
  challenge_public_id: z.string().nullable().optional(),
  first_touch_source: z.string().nullable().optional(),
  first_touch_medium: z.string().nullable().optional(),
  first_touch_campaign: z.string().nullable().optional(),
  first_touch_utm_id: z.string().nullable().optional(),
  latest_touch_source: z.string().nullable().optional(),
  latest_touch_medium: z.string().nullable().optional(),
  latest_touch_campaign: z.string().nullable().optional(),
  latest_touch_utm_id: z.string().nullable().optional(),
});

export const ChallengeResponseSchema = z.object({
  challenge_id: UuidSchema,
  public_id: z.string(),
  challenge_url: z.string().url(),
  landing_path: z.string(),
  session_id: UuidSchema,
  attempt_id: UuidSchema,
  attempt_composite_id: UuidSchema.nullable().optional(),
  robot_id: UuidSchema,
  robot_name: z.string().nullable().optional(),
  game_type_id: UuidSchema,
  game_type_key: z.string().nullable().optional(),
  share_kind: z.string(),
  preview_url: z.string().url().nullable().optional(),
  thumbnail_url: z.string().url().nullable().optional(),
  default_utm_source: z.string(),
  default_utm_medium: z.string(),
  default_utm_campaign: z.string(),
  default_utm_content: z.string(),
  default_utm_id: z.string(),
  cta_headline: z.string(),
  cta_label: z.string(),
  was_created: z.boolean(),
  created_at: z.string(),
});

export const ChallengeShareSummarySchema = ChallengeResponseSchema;
export const ChallengeLandingResponseSchema = ChallengeResponseSchema;

export const CreateChallengeRequestSchema = z.object({
  attempt_id: UuidSchema,
  share_target: z.string().nullable().optional(),
});

export const ChallengeShareInitiationMethodSchema = z.enum([
  'native_share',
  'download',
  'copy_link',
]);

export const ChallengeShareTargetSchema = z.enum([
  'direct',
  'instagram',
  'facebook',
  'tiktok',
]);

export const RecordChallengeShareInitiationRequestSchema = z.object({
  method: ChallengeShareInitiationMethodSchema,
  share_target: ChallengeShareTargetSchema.optional(),
});

export const RecordChallengeShareInitiationResponseSchema = z.object({
  challenge_id: UuidSchema,
  method: ChallengeShareInitiationMethodSchema,
  share_target: ChallengeShareTargetSchema.optional(),
  recorded: z.literal(true),
});

export const CreateLandingTouchRequestSchema = z.object({
  landing_surface_type: LandingSurfaceTypeSchema,
  landing_surface_key: z.string().nullable().optional(),
  challenge_public_id: z.string().nullable().optional(),
  anon_id: UuidSchema,
  landing_url: z.string().url(),
  referrer_url: z.string().nullable().optional(),
  utm_source: z.string().nullable().optional(),
  utm_medium: z.string().nullable().optional(),
  utm_campaign: z.string().nullable().optional(),
  utm_id: z.string().nullable().optional(),
  utm_content: z.string().nullable().optional(),
  utm_term: z.string().nullable().optional(),
  fbclid: z.string().nullable().optional(),
  ttclid: z.string().nullable().optional(),
  twclid: z.string().nullable().optional(),
  li_fat_id: z.string().nullable().optional(),
  gclid: z.string().nullable().optional(),
  gbraid: z.string().nullable().optional(),
  wbraid: z.string().nullable().optional(),
  msclkid: z.string().nullable().optional(),
  aleid: z.string().nullable().optional(),
});

export const CreateLandingTouchResponseSchema = z.object({
  landing_touch_id: UuidSchema,
  anon_id: UuidSchema,
  attribution_token: z.string(),
  challenge_share_id: UuidSchema.nullable().optional(),
  challenge_public_id: z.string().nullable().optional(),
  created: z.boolean(),
  deduped: z.boolean(),
});

export const ClaimLandingTouchRequestSchema = z.object({
  attribution_token: z.string().min(1),
});

export const ClaimLandingTouchResponseSchema = z.object({
  landing_touch_id: UuidSchema,
  user_id: UuidSchema,
  claim_stage: z.string(),
  is_first_touch_for_user: z.boolean(),
  challenge_share_id: UuidSchema.nullable().optional(),
  challenge_public_id: z.string().nullable().optional(),
  attribution_context: AttributionContextSchema,
});

export const ResolveAttributionRequestSchema = z.object({
  attribution_token: z.string().min(1),
});

export const ResolveAttributionResponseSchema = z.object({
  landing_touch_id: UuidSchema,
  attribution_context: AttributionContextSchema,
});

export const AttributionTokenPayloadSchema = z.object({
  landing_touch_id: UuidSchema,
});

export type LandingSurfaceType = z.infer<typeof LandingSurfaceTypeSchema>;
export type AttributionContext = z.infer<typeof AttributionContextSchema>;
export type ChallengeResponse = z.infer<typeof ChallengeResponseSchema>;
export type ChallengeShareInitiationMethod = z.infer<
  typeof ChallengeShareInitiationMethodSchema
>;
export type ChallengeShareTarget = z.infer<typeof ChallengeShareTargetSchema>;
export type CreateChallengeRequest = z.infer<typeof CreateChallengeRequestSchema>;
export type RecordChallengeShareInitiationRequest = z.infer<
  typeof RecordChallengeShareInitiationRequestSchema
>;
export type RecordChallengeShareInitiationResponse = z.infer<
  typeof RecordChallengeShareInitiationResponseSchema
>;
export type CreateLandingTouchRequest = z.infer<typeof CreateLandingTouchRequestSchema>;
export type CreateLandingTouchResponse = z.infer<typeof CreateLandingTouchResponseSchema>;
export type ClaimLandingTouchRequest = z.infer<typeof ClaimLandingTouchRequestSchema>;
export type ClaimLandingTouchResponse = z.infer<typeof ClaimLandingTouchResponseSchema>;
export type ResolveAttributionRequest = z.infer<typeof ResolveAttributionRequestSchema>;
export type ResolveAttributionResponse = z.infer<typeof ResolveAttributionResponseSchema>;
export type AttributionTokenPayload = z.infer<typeof AttributionTokenPayloadSchema>;
